"use client";
import { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import ReCAPTCHA from "react-google-recaptcha";
import dynamic from "next/dynamic";

import GreenCheck from "assets/svg/green-check-circle.svg";

import { validEmail } from "helpers/form-validator";
import { firstCharControl } from "helpers/first-char-control";
import { Ga4WithEventParameters } from "src/helpers/google-tag-manager/events/ga4-with-eventparameters";

import { validationWrongEmailAddress } from "constants/form-elements";

import ExplicitConsent from "modal-contents/explicit-consent";

import Button from "components/common/button";
import Input from "components/common/input";
import CheckBoxItem from "components/common/checkbox-item";
import ModalTextContent from "components/modal-text-content";

import styles from "./styles.module.css";

const Modal = dynamic(() => import("components/common/modal"), { ssr: false });

const UserFormScreen = ({ handleSend, listingId }) => {
  const recaptchaRef = useRef();
  const [userFormValues, setUserFormValues] = useState({
    name: "",
    phone: "",
    email: "",
  });
  const [isKvkkApproved, setIsKvkkApproved] = useState(false);
  const [isKvkkModalOpened, setIsKvkkModalOpened] = useState(false);
  const [isWrongEmail, setIsWrongEmail] = useState(false);

  const handleKvkkChange = (e) => setIsKvkkApproved(!isKvkkApproved);
  const handleUserFormChange = (key, value) => {
    const firstChar = value.charAt(0);
    key === "email" && setIsWrongEmail(false);
    switch (key) {
      case "phone":
        setUserFormValues({ ...userFormValues, [key]: firstCharControl(firstChar, "5") ? "" : value });
        break;

      default:
        setUserFormValues({ ...userFormValues, [key]: value });
        break;
    }
  };

  const triggerKvkkModalOpened = () => setIsKvkkModalOpened(!isKvkkModalOpened);

  const handleButton = () => {
    if (validEmail(userFormValues.email)) {
      return handleSend(userFormValues, isKvkkApproved, recaptchaRef);
    }
    setIsWrongEmail(true);
  };

  useEffect(() => {
    Ga4WithEventParameters({
      eventName: "report_item",
      eventParameters: { item_id: listingId.toString() },
    });
  }, []);

  return (
    <>
      <div className={styles.resultIconWrap}>
        <GreenCheck />
      </div>
      <span className={styles.title}>Uyarınız bize ulaştı, teşekkürler!</span>
      <span className={styles.subInfo}>
        Şikayetinizle ilgili gelişmelerden sizi haberdar edebilmemiz için iletişim bilgilerinizi bırakabilirsiniz.
      </span>
      <div className={styles.userFormWrap}>
        <div className={styles.formGroup}>
          <span className={styles.label}>Ad Soyad</span>
          <Input
            onChange={(e) => handleUserFormChange("name", e.target.value)}
            value={userFormValues.name}
            placeholder={"Ad Soyad Giriniz"}
          />
        </div>
        <div className={styles.formGroup}>
          <span className={styles.label}>Telefon</span>
          <Input
            onChange={(e) => handleUserFormChange("phone", e.target.value)}
            value={userFormValues.phone}
            placeholder={"(5xx) xxx xx xx"}
            phone
          />
        </div>
        <div className={styles.formGroup}>
          <span className={styles.label}>E-Posta</span>
          <Input
            onChange={(e) => handleUserFormChange("email", e.target.value)}
            value={userFormValues.email}
            placeholder={"E-Posta Giriniz"}
          />
          {isWrongEmail && <span className={styles.error}>{validationWrongEmailAddress}</span>}
        </div>
        <div className={styles.formGroup}>
          <CheckBoxItem
            title={
              <div className={styles.checkItem}>
                Verilerimin <span onClick={triggerKvkkModalOpened}>Açık Rıza Metni</span>`nde belirtilen şekilde
                işlenmesine onay veriyorum.*
              </div>
            }
            name={"isKvkkApproved"}
            onChange={handleKvkkChange}
          />
        </div>
        <div className={styles.formGroup}>
          <ReCAPTCHA
            key="captcha"
            ref={recaptchaRef}
            sitekey={window.env.EJ_KEYS_RECAPTCHA}
            size="invisible"
            badge="inline"
          />
        </div>
        <div className={styles.formGroup}>
          <Button
            size="M"
            disable={
              userFormValues.name === "" ||
              userFormValues.phone === "" ||
              userFormValues.email === "" ||
              !isKvkkApproved
            }
            onClick={handleButton}
          >
            Gönder
          </Button>
        </div>
      </div>
      <Modal isOpen={isKvkkModalOpened} handleClose={triggerKvkkModalOpened}>
        <ModalTextContent>
          <ExplicitConsent />
        </ModalTextContent>
      </Modal>
    </>
  );
};

UserFormScreen.propTypes = {
  handleSend: PropTypes.func,
  listingId: PropTypes.number,
};

export default UserFormScreen;
